/**
 * Custom hook for managing modal state
 * Simplifies modal open/close logic and associated data management
 */

import { useState, useCallback } from 'react';

export interface UseModalReturn<T = any> {
  isOpen: boolean;
  data: T | null;
  open: (modalData?: T) => void;
  close: () => void;
  setData: (data: T | null) => void;
  toggle: () => void;
}

/**
 * Hook for managing modal state with optional data
 *
 * @example
 * const approvalModal = useModal<LessonRequest>();
 * const rejectionModal = useModal<LessonRequest>();
 *
 * // Open modal with data
 * approvalModal.open(selectedRequest);
 *
 * // Access modal state
 * <Modal isOpen={approvalModal.isOpen} onClose={approvalModal.close}>
 *   {approvalModal.data && (
 *     <div>Processing request for {approvalModal.data.name}</div>
 *   )}
 * </Modal>
 */
export const useModal = <T = any>(initialData?: T): UseModalReturn<T> => {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<T | null>(initialData || null);

  const open = useCallback((modalData?: T) => {
    if (modalData !== undefined) {
      setData(modalData);
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return {
    isOpen,
    data,
    open,
    close,
    setData,
    toggle,
  };
};

/**
 * Hook for managing multiple related modals
 * Useful when you have several modals that shouldn't be open simultaneously
 *
 * @example
 * const modals = useModalGroup(['approval', 'rejection', 'detail']);
 *
 * // Open a specific modal with data
 * modals.open('approval', selectedRequest);
 *
 * // Check if modal is open
 * <Modal isOpen={modals.isOpen('approval')} onClose={() => modals.close('approval')}>
 *   ...
 * </Modal>
 */
export const useModalGroup = <T = any>(
  modalNames: string[]
): {
  open: (name: string, data?: T) => void;
  close: (name: string) => void;
  closeAll: () => void;
  isOpen: (name: string) => boolean;
  getData: (name: string) => T | null;
  setData: (name: string, data: T | null) => void;
} => {
  const [openModals, setOpenModals] = useState<Set<string>>(new Set());
  const [modalData, setModalData] = useState<Map<string, T>>(new Map());

  const open = useCallback((name: string, data?: T) => {
    setOpenModals((prev) => new Set(prev).add(name));
    if (data !== undefined) {
      setModalData((prev) => new Map(prev).set(name, data));
    }
  }, []);

  const close = useCallback((name: string) => {
    setOpenModals((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  }, []);

  const closeAll = useCallback(() => {
    setOpenModals(new Set());
  }, []);

  const isOpen = useCallback(
    (name: string) => {
      return openModals.has(name);
    },
    [openModals]
  );

  const getData = useCallback(
    (name: string): T | null => {
      return modalData.get(name) || null;
    },
    [modalData]
  );

  const setData = useCallback((name: string, data: T | null) => {
    setModalData((prev) => {
      const next = new Map(prev);
      if (data === null) {
        next.delete(name);
      } else {
        next.set(name, data);
      }
      return next;
    });
  }, []);

  return {
    open,
    close,
    closeAll,
    isOpen,
    getData,
    setData,
  };
};
